import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Chip,
    Dropdown,
    Label,
    ListBox,
    Pagination,
} from '@heroui/react';
import { DataGrid, InlineSelect, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RefLabel } from '@/components/refLabel';
import type { BranchRef } from '../userFormModal';

/** What an org-wide user's branch cell reads. */
const EMPTY_BRANCH = '—';
import {
    ROLE_LABEL_KEY,
    STATUS_LABEL_KEY,
    TEAM_PAGE_SIZE_OPTIONS,
    USER_COLUMN,
    USER_ROW_ACTION_META,
    getPaginationWindow,
    getUserRowActions,
    type User,
    type UserRowAction,
} from '../teamMeta';

type UserTableProps = {
    data: User[];
    callerId: string | null;
    showBranchColumn: boolean;
    branchRefById: (id: string | null) => BranchRef | null;
    onEditUser: (id: string) => void;
    onOpenPin: (id: string) => void;
    onOpenPassword: (id: string) => void;
    onDeleteUser: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

/**
 * Render-only users grid. The data is **server-paginated** — `data` is already
 * the current page in the server's order (by name), so there's no client-side
 * sort or slice here; the footer just drives the page / page-size controls.
 * Columns aren't sortable because the API has no sort parameter.
 */
export const UserTable: React.FC<UserTableProps> = ({
    data,
    callerId,
    showBranchColumn,
    branchRefById,
    onEditUser,
    onOpenPin,
    onOpenPassword,
    onDeleteUser,
    onPageChange,
    onPageSizeChange,
    page,
    pageSize,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            tableAria: t('team.components.userTable.tableAria'),
            actionsAria: t('team.components.userTable.actionsAria'),
            previous: t('team.components.userTable.pagination.previous'),
            next: t('team.components.userTable.pagination.next'),
            perPage: t('team.components.userTable.pagination.perPage'),
            columns: {
                name: t('team.components.userTable.columns.name'),
                email: t('team.components.userTable.columns.email'),
                role: t('team.components.userTable.columns.role'),
                branch: t('team.components.userTable.columns.branch'),
                status: t('team.components.userTable.columns.status'),
                hasPin: t('team.components.userTable.columns.hasPin'),
            },
            emptyTitle: t('team.components.userTable.empty.title'),
            noEmail: t('team.components.userTable.noEmail'),
            hasPinLegend: t('team.components.userTable.hasPinLegend'),
            pinSet: t('team.components.userTable.pinSet'),
            pinUnset: t('team.components.userTable.pinUnset'),
        }),
        [t],
    );

    const onRowAction = React.useCallback(
        (id: string, action: UserRowAction) => {
            switch (action) {
                case 'edit':
                    onEditUser(id);
                    return;
                case 'setPin':
                    onOpenPin(id);
                    return;
                case 'resetPassword':
                    onOpenPassword(id);
                    return;
                case 'delete':
                    onDeleteUser(id);
                    return;
            }
        },
        [onDeleteUser, onEditUser, onOpenPassword, onOpenPin],
    );

    const columns = React.useMemo<DataGridColumn<User>[]>(() => {
        const cols: DataGridColumn<User>[] = [
            {
                id: USER_COLUMN.name,
                header: copy.columns.name,
                accessorKey: 'name',
                isRowHeader: true,
                cell: (item) => (
                    <span
                        className="text-sm font-medium text-foreground"
                        {...getNodeTestIdProp(`user-row-name-${item.id}`)}
                    >
                        {item.name}
                    </span>
                ),
            },
            {
                id: USER_COLUMN.email,
                header: copy.columns.email,
                accessorKey: 'email',
                cell: (item) => (
                    <span
                        className="text-sm text-foreground"
                        {...getNodeTestIdProp(`user-row-email-${item.id}`)}
                    >
                        {item.email ?? copy.noEmail}
                    </span>
                ),
            },
            {
                id: USER_COLUMN.role,
                header: copy.columns.role,
                accessorKey: 'role',
                width: 130,
                cell: (item) => (
                    <Chip
                        color="default"
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp(`user-row-role-${item.id}`)}
                    >
                        {t(ROLE_LABEL_KEY[item.role])}
                    </Chip>
                ),
            },
        ];

        if (showBranchColumn) {
            cols.push({
                id: USER_COLUMN.branch,
                header: copy.columns.branch,
                width: 160,
                cell: (item) => {
                    const branch = branchRefById(item.branchId);
                    return (
                        <span
                            className="text-sm text-foreground"
                            {...getNodeTestIdProp(`user-row-branch-${item.id}`)}
                        >
                            {branch === null ? (
                                EMPTY_BRANCH
                            ) : (
                                <RefLabel
                                    archived={branch.archived}
                                    id={branch.id}
                                    name={branch.name}
                                />
                            )}
                        </span>
                    );
                },
            });
        }

        cols.push(
            {
                id: USER_COLUMN.status,
                header: copy.columns.status,
                accessorKey: 'status',
                width: 110,
                align: 'center',
                cell: (item) => (
                    <Chip
                        color={item.status === 'Active' ? 'success' : 'default'}
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp(`user-row-status-${item.id}`)}
                    >
                        {t(STATUS_LABEL_KEY[item.status])}
                    </Chip>
                ),
            },
            {
                id: USER_COLUMN.hasPin,
                // A column headed "PIN" over a tick and a dash says nothing
                // about what a PIN is for here (BOS-170). The legend is on the
                // header, where a reader wondering about a column looks.
                header: (
                    <span title={copy.hasPinLegend}>
                        {copy.columns.hasPin}
                        <span className="sr-only">
                            {` — ${copy.hasPinLegend}`}
                        </span>
                    </span>
                ),
                accessorKey: 'hasPin',
                width: 90,
                align: 'center',
                cell: (item) =>
                    item.hasPin ? (
                        <Icon
                            aria-label={copy.pinSet}
                            className="size-4 text-success"
                            icon="gravity-ui:circle-check-fill"
                            {...getNodeTestIdProp(
                                `user-row-pin-set-${item.id}`,
                            )}
                        />
                    ) : (
                        <span
                            className="text-sm text-muted"
                            aria-label={copy.pinUnset}
                            {...getNodeTestIdProp(
                                `user-row-pin-unset-${item.id}`,
                            )}
                        >
                            —
                        </span>
                    ),
            },
            {
                id: USER_COLUMN.actions,
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => {
                    const actions = getUserRowActions(item, callerId);
                    return (
                        <div
                            className="flex justify-center"
                            {...getNodeTestIdProp(
                                `user-row-actions-${item.id}`,
                            )}
                        >
                            <Dropdown>
                                <Button
                                    aria-label={copy.actionsAria}
                                    isIconOnly
                                    size="sm"
                                    variant="tertiary"
                                    {...getNodeTestIdProp(
                                        `user-row-actions-trigger-${item.id}`,
                                    )}
                                >
                                    <Icon icon="gravity-ui:ellipsis-vertical" />
                                </Button>
                                <Dropdown.Popover className="min-w-[180px]">
                                    <Dropdown.Menu
                                        onAction={(key) =>
                                            onRowAction(
                                                item.id,
                                                key as UserRowAction,
                                            )
                                        }
                                    >
                                        {actions.map((action) => (
                                            <Dropdown.Item
                                                id={action}
                                                key={action}
                                                textValue={t(
                                                    USER_ROW_ACTION_META[action]
                                                        .labelKey,
                                                )}
                                                variant={
                                                    USER_ROW_ACTION_META[action]
                                                        .variant
                                                }
                                                {...getNodeTestIdProp(
                                                    `user-row-action-${action}-${item.id}`,
                                                )}
                                            >
                                                <Icon
                                                    icon={
                                                        USER_ROW_ACTION_META[
                                                            action
                                                        ].icon
                                                    }
                                                />
                                                <Label>
                                                    {t(
                                                        USER_ROW_ACTION_META[
                                                            action
                                                        ].labelKey,
                                                    )}
                                                </Label>
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown.Popover>
                            </Dropdown>
                        </div>
                    );
                },
            },
        );

        return cols;
    }, [branchRefById, callerId, copy, onRowAction, showBranchColumn, t]);

    const pageWindow = getPaginationWindow(totalPages, page);

    return (
        <Widget
            className="flex flex-col gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp('user-table-widget')}
        >
            <DataGrid<User>
                aria-label={copy.tableAria}
                columns={columns}
                data={data}
                getRowId={(item) => item.id}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('user-table-empty')}
                    >
                        <Icon
                            aria-hidden
                            className="size-8 text-default-500"
                            icon="gravity-ui:persons"
                        />
                        <span
                            className="text-sm"
                            {...getNodeTestIdProp('user-table-empty-title')}
                        >
                            {copy.emptyTitle}
                        </span>
                    </div>
                )}
                scrollContainerClassName="max-h-[600px] overflow-y-auto"
                variant="primary"
                {...getNodeTestIdProp('user-table-grid')}
            />
            {totalCount > TEAM_PAGE_SIZE_OPTIONS[0]! ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-separator p-3"
                    {...getNodeTestIdProp('user-table-pagination')}
                >
                    <div className="flex items-center gap-2 text-xs text-muted">
                        <span>{copy.perPage}</span>
                        <InlineSelect
                            aria-label={copy.perPage}
                            onChange={(value) =>
                                value && onPageSizeChange(Number(value))
                            }
                            value={String(pageSize)}
                            {...getNodeTestIdProp('user-table-per-page')}
                        >
                            <InlineSelect.Trigger>
                                <InlineSelect.Value />
                                <InlineSelect.Indicator />
                            </InlineSelect.Trigger>
                            <InlineSelect.Popover className="w-[96px]">
                                <ListBox>
                                    {TEAM_PAGE_SIZE_OPTIONS.map((size) => (
                                        <ListBox.Item
                                            id={String(size)}
                                            key={size}
                                            textValue={String(size)}
                                        >
                                            {size}
                                            <ListBox.ItemIndicator />
                                        </ListBox.Item>
                                    ))}
                                </ListBox>
                            </InlineSelect.Popover>
                        </InlineSelect>
                    </div>
                    {totalPages > 1 ? (
                        <Pagination size="sm">
                            <Pagination.Content>
                                <Pagination.Item>
                                    <Pagination.Previous
                                        aria-label={copy.previous}
                                        isDisabled={page === 1}
                                        onPress={() => onPageChange(page - 1)}
                                    >
                                        <Pagination.PreviousIcon />
                                    </Pagination.Previous>
                                </Pagination.Item>
                                {pageWindow.map((entry, index) =>
                                    entry === 'ellipsis' ? (
                                        <Pagination.Item
                                            key={`user-table-page-ellipsis-${index}`}
                                        >
                                            <Pagination.Ellipsis />
                                        </Pagination.Item>
                                    ) : (
                                        <Pagination.Item key={entry}>
                                            <Pagination.Link
                                                isActive={entry === page}
                                                onPress={() =>
                                                    onPageChange(entry)
                                                }
                                                {...getNodeTestIdProp(
                                                    `user-table-page-${entry}`,
                                                )}
                                            >
                                                {entry}
                                            </Pagination.Link>
                                        </Pagination.Item>
                                    ),
                                )}
                                <Pagination.Item>
                                    <Pagination.Next
                                        aria-label={copy.next}
                                        isDisabled={page === totalPages}
                                        onPress={() => onPageChange(page + 1)}
                                    >
                                        <Pagination.NextIcon />
                                    </Pagination.Next>
                                </Pagination.Item>
                            </Pagination.Content>
                        </Pagination>
                    ) : null}
                </div>
            ) : null}
        </Widget>
    );
};
